import Modal from "@material-ui/core/Modal";
import {
  EMPLOYEE_CATEGORY_OPTIONS,
  EMPLOYMENT_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  SALARY_OPTIONS,
} from "model/OrganizationalStructure";
import {
  CompanyEmploymentTaxType,
  CompanyWorkerEmploymentSalaryV3Response,
  CompanyWorkerEmploymentV3Response,
  CompanyWorkerSalary,
  CompanyWorkerTaxV3Response,
  EmployeeCategory,
  SalaryFormData,
  UpdateEmploymentV3Request,
  WorkerEmploymentType,
  WorkerSalaryType,
} from "model/Salary";
import { PaymentMethod } from "model/Gig";
import { isSettingEnabled } from "model/settings.logic";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";
import { ButtonComponent } from "../general/button/button.component";
import { DatePickerWithLabel } from "../general/datePickerWithLabel/datePickerWithLabel.component";
import { DropdownWithLabel } from "../general/dropDownWithLabel/dropdownWithLabel.component";
import { TextInputWithLabel } from "../general/textInput/textInput.component";
import { TopModal } from "../general/topModal/topModal.component";
import {
  formatDate,
  getFirstDayOfCurrentMonth,
} from "../organizationalStructure/createWorkerModal.component";
import { ModalBody, UpdateSalaryContainer } from "./salary.styled";
import axios from "axios";
import { Api } from "services/api/api.service";
import { CustomIcon } from "components/icon/customIcon.component";
import { Color } from "config/colors";

type Props = {
  setShowUpdateWorkerSalaryModal: React.Dispatch<React.SetStateAction<boolean>>;
  showUpdateWorkerSalaryModal: boolean;
  selectedWorkerWithSalary: CompanyWorkerSalary;
  currentPage: number;
  pageSize: number;
};

export const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

export const taxTableOptions = Array.from({ length: 36 - 29 + 1 }, (_, i) => {
  const value = 29 + i;
  return {
    value: String(value),
    label: String(value),
  };
});

const normalizeDateTimeNoFractionNoOffset = (value: string): string => {
  if (!value) return value;

  let v = value;

  v = v.replace(/(Z|[+-]\d{2}:\d{2})$/, "");
  v = v.replace(/\.\d+$/, "");

  return v;
};

const WORKER_EMPLOYMENT_TYPES = new Set<string>(
  Object.values(WorkerEmploymentType),
);
const WORKER_SALARY_TYPES = new Set<string>(Object.values(WorkerSalaryType));
const EMPLOYEE_CATEGORIES = new Set<string>(Object.values(EmployeeCategory));
const PAYMENT_METHODS = new Set<string>(Object.values(PaymentMethod));

const isWorkerEmploymentType = (value: string): value is WorkerEmploymentType =>
  WORKER_EMPLOYMENT_TYPES.has(value);
const isWorkerSalaryType = (value: string): value is WorkerSalaryType =>
  WORKER_SALARY_TYPES.has(value);
const isEmployeeCategory = (value: string): value is EmployeeCategory =>
  EMPLOYEE_CATEGORIES.has(value);
const isPaymentMethod = (value: string): value is PaymentMethod =>
  PAYMENT_METHODS.has(value);

export const UpdateWorkerSalaryModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [, monthSalaryActions] = useMonthSalaryStore();
  const [loading, setLoading] = useState(false);
  const hasEnded =
    !!props.selectedWorkerWithSalary.endDate &&
    Date.now() > new Date(props.selectedWorkerWithSalary.endDate).getTime();

  const initialFormData: SalaryFormData = {
    employmentType: undefined,
    salaryType: undefined,
    salary: 0,
    startDate: normalizeDateTimeNoFractionNoOffset(getFirstDayOfCurrentMonth()),
    endDate: normalizeDateTimeNoFractionNoOffset(getFirstDayOfCurrentMonth()),
    workPercentage: 1,
    taxTable: 36,
    paymentType: PaymentMethod.NONE,
    role: "-",
    employeeCategory: undefined,
    employeeNumber: 0,
  };

  const [formData, setFormData] = useState<SalaryFormData>(initialFormData);

  const [v3Employment, setV3Employment] =
    useState<CompanyWorkerEmploymentV3Response | null>(null);
  const [v3Salary, setV3Salary] =
    useState<CompanyWorkerEmploymentSalaryV3Response | null>(null);
  const [v3Tax, setV3Tax] = useState<CompanyWorkerTaxV3Response | null>(null);

  useEffect(() => {
    const companyId = companyState.company?.id;
    const workerId = props.selectedWorkerWithSalary?.workerId;
    if (!companyId || !workerId) return;

    let isMounted = true;
    Api()
      .company.worker.getCompanyWorker(companyId, workerId)
      .then((res) => {
        if (!isMounted) return;
        const employments = res.data.employments ?? [];
        const taxes = res.data.taxes ?? [];
        const employment =
          employments.find((e) => e.isActive) ?? employments[0] ?? null;
        const salary =
          employment?.salaries.find((s) => s.isActive) ??
          employment?.salaries[0] ??
          null;
        const tax = taxes.find((t) => t.isActive) ?? taxes[0] ?? null;
        setV3Employment(employment);
        setV3Salary(salary);
        setV3Tax(tax);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [companyState.company?.id, props.selectedWorkerWithSalary?.workerId]);

  const isTraineeEmployee =
    formData.employmentType === WorkerEmploymentType.TRAINEE;

  const isConsultantEmployee =
    formData.employmentType === WorkerEmploymentType.CONSULTANT;

  const isProjectEmployee =
    formData.employmentType === WorkerEmploymentType.PROJECT_EMPLOYEE;

  const employmentOptions = EMPLOYMENT_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const salaryOptions = SALARY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const employeeCategoryOptions = EMPLOYEE_CATEGORY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  useEffect(() => {
    if (!props.selectedWorkerWithSalary) return;

    const employment = props.selectedWorkerWithSalary.employment;

    const rawEmploymentType = employment?.employmentType;
    const rawSalaryType = employment?.salaryType;
    const rawPaymentType = employment?.paymentType;
    const rawEmployeeCategory = employment?.employeeCategory;

    setFormData((prev) => ({
      ...prev,
      employmentType:
        rawEmploymentType && isWorkerEmploymentType(rawEmploymentType)
          ? rawEmploymentType
          : undefined,
      salaryType:
        rawSalaryType && isWorkerSalaryType(rawSalaryType)
          ? rawSalaryType
          : undefined,
      salary: employment?.salary || 0,
      workPercentage: employment?.workPercentage || 1,
      taxTable: employment?.taxValue || 36,
      paymentType:
        rawPaymentType && isPaymentMethod(rawPaymentType)
          ? rawPaymentType
          : PaymentMethod.NONE,
      role: "-",
      startDate: normalizeDateTimeNoFractionNoOffset(
        employment?.startDate || prev.startDate,
      ),
      endDate: (employment?.endDate ?? prev?.endDate)?.includes("9999-12-31")
        ? null
        : (employment?.endDate ?? prev?.endDate)
          ? normalizeDateTimeNoFractionNoOffset(
              employment?.endDate ?? prev?.endDate,
            )
          : null,
      employeeCategory:
        rawEmployeeCategory && isEmployeeCategory(rawEmployeeCategory)
          ? rawEmployeeCategory
          : employeeCategoryOptions[0].value,
      employeeNumber: employment?.employeeNumber || 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedWorkerWithSalary]);

  const [subscription, setSubscription] = useState<string | undefined>(
    undefined,
  );

  const getEmployeeNumber = () => {
    if (!companyState.company?.id) return;
    Api()
      .company.organizationalStructure.getEmployeeNumber(
        companyState.company?.id,
      )
      .then((res) => {
        setFormData((prev) => ({
          ...prev,
          employeeNumber: res.data,
        }));
      })
      .catch(() => {
        setFormData((prev) => ({
          ...prev,
          employeeNumber: 0,
        }));
      });
  };

  useEffect(() => {
    if (!companyState.company?.id) return;

    Api()
      .company.getCompanySubscriptionInfo(companyState.company.id)
      .then((res) => {
        setSubscription(res.data.membershipDisplayName);
      })
      .catch(() => {
        setSubscription(undefined);
      });
  }, [companyState.company?.id]);

  const isFlexCompany = useMemo(() => {
    if (subscription === undefined || subscription === null) return false;
    return subscription.includes("Flex");
  }, [subscription]);

  const hasSimflexityPay = useMemo(() => {
    const settings = companyState.company?.companySettings ?? [];
    return isSettingEnabled(settings, "SimflexityPay");
  }, [companyState.company?.companySettings]);

  const isSimflexityEmployee =
    formData.employmentType === WorkerEmploymentType.SIMFLEXITY_EMPLOYEE;

  const salaryOptionsFiltered = useMemo(() => {
    if (!isSimflexityEmployee) return salaryOptions;
    return salaryOptions.filter(
      (opt) => opt.value !== WorkerSalaryType.MONTHLY,
    );
  }, [isSimflexityEmployee, salaryOptions]);

  useEffect(() => {
    if (!isSimflexityEmployee) return;

    setFormData((prev) => ({
      ...prev,
      salaryType: WorkerSalaryType.HOURLY,
    }));
  }, [isSimflexityEmployee, formData.salaryType]);

  const canSelectSimflexityPay = useMemo(() => {
    if (isSimflexityEmployee) return true;
    return (
      isFlexCompany &&
      hasSimflexityPay &&
      formData.salaryType === WorkerSalaryType.HOURLY
    );
  }, [
    isSimflexityEmployee,
    isFlexCompany,
    hasSimflexityPay,
    formData.salaryType,
  ]);

  const paymentTypeOptions = useMemo(() => {
    const base = PAYMENT_TYPE_OPTIONS.map((option) => ({
      ...option,
      label: t(option.label),
    }));

    return base.filter((opt) => {
      if (opt.value === PaymentMethod.SIMFLEXITY_PAY)
        return canSelectSimflexityPay;
      return true;
    });
  }, [t, canSelectSimflexityPay]);

  useEffect(() => {
    if (!isSimflexityEmployee) return;
    if (formData.paymentType === PaymentMethod.SIMFLEXITY_PAY) return;

    setFormData((prev) => ({
      ...prev,
      paymentType: PaymentMethod.SIMFLEXITY_PAY,
    }));
  }, [isSimflexityEmployee, formData.paymentType]);

  useEffect(() => {
    if (isSimflexityEmployee) return;
    if (formData.paymentType !== PaymentMethod.SIMFLEXITY_PAY) return;
    if (canSelectSimflexityPay) return;

    setFormData((prev) => ({
      ...prev,
      paymentType: PaymentMethod.NONE,
    }));
  }, [isSimflexityEmployee, formData.paymentType, canSelectSimflexityPay]);

  const handleUpdateSalary = async () => {
    const companyId = companyState.company?.id;
    const workerId = props.selectedWorkerWithSalary.workerId;
    if (
      !companyId ||
      !workerId ||
      !formData.employmentType ||
      !formData.salaryType ||
      !formData.employeeCategory ||
      !v3Employment ||
      !v3Salary ||
      !v3Tax
    )
      return;

    setLoading(true);

    const employmentBody: UpdateEmploymentV3Request = {
      startDate: normalizeDateTimeNoFractionNoOffset(formData.startDate),
      endDate:
        formData.endDate === null
          ? null
          : normalizeDateTimeNoFractionNoOffset(formData.endDate),
      employmentType: formData.employmentType,
      employeeNumber: formData.employeeNumber,
      employeeCategory: formData.employeeCategory,
      salaryType: formData.salaryType,
      workPercentage: formData.workPercentage,
      paymentType: formData.paymentType,
      role: formData.role,
      // Preserve v3-only fields not yet exposed in the UI.
      agreement: v3Employment.agreement,
      vacationDays: v3Employment.vacationDays,
      noticeDays: v3Employment.noticeDays,
      workplace: v3Employment.workplace,
      probationPeriodEndDate: v3Employment.probationPeriodEndDate,
    };

    const isPercentTax = formData.paymentType === PaymentMethod.SIMFLEXITY_PAY;
    const nextTaxType = isPercentTax
      ? CompanyEmploymentTaxType.PERCENTAGE
      : CompanyEmploymentTaxType.TABLE;

    const salaryChanged = formData.salary !== v3Salary.value;
    const taxChanged =
      nextTaxType !== v3Tax.type || formData.taxTable !== v3Tax.value;

    const failedSteps: string[] = [];
    let employmentErrorMessage: string | undefined;

    try {
      await Api().company.worker.updateEmployment(
        companyId,
        workerId,
        v3Employment.id,
        employmentBody,
      );
    } catch (e) {
      failedSteps.push("employment");
      if (axios.isAxiosError(e)) {
        const errorMessage = e.response?.data?.errorMessage;
        if (typeof errorMessage === "string") {
          employmentErrorMessage = errorMessage;
        }
      }
    }

    if (salaryChanged && !failedSteps.includes("employment")) {
      try {
        await Api().company.worker.updateEmploymentSalary(
          companyId,
          workerId,
          v3Employment.id,
          v3Salary.id,
          {
            startDate: v3Salary.startDate,
            value: formData.salary,
          },
        );
      } catch {
        failedSteps.push("salary");
      }
    }

    if (taxChanged && !failedSteps.includes("employment")) {
      try {
        await Api().company.worker.updateWorkerTax(
          companyId,
          workerId,
          v3Tax.id,
          {
            startDate: v3Tax.startDate,
            taxType: nextTaxType,
            value: formData.taxTable,
          },
        );
      } catch {
        failedSteps.push("tax");
      }
    }

    if (failedSteps.length === 0) {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t("Salary.Changed"),
          message: t("Salary.ChangedWorkerEmployment"),
        },
      });
      props.setShowUpdateWorkerSalaryModal(false);
      monthSalaryActions
        .getAllSalaries(companyId, props.currentPage, props.pageSize)
        ?.catch?.(() => {});
    } else {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message:
            employmentErrorMessage === "Employee number is already in use"
              ? t("Alerts.EmployeeNumberIsAlreadyInUse")
              : employmentErrorMessage === "Selected start date is not allowed"
                ? t("Alerts.DateNotAllowed")
                : t("Alerts.SomethingWentWrong"),
        },
      });
    }

    setLoading(false);
  };

  const validateFields = () => {
    const basicFields = [
      formData.employmentType,
      formData.salaryType,
      formData.startDate,
      formData.role,
    ];

    if (basicFields.some((field) => !field)) return false;
    if (!isTraineeEmployee && formData.salary <= 0) return false;
    if ((isTraineeEmployee || isProjectEmployee) && formData.endDate === null)
      return false;
    if (formData.endDate !== null && formData.endDate < formData.startDate)
      return false;

    if (formData.salaryType === WorkerSalaryType.MONTHLY) {
      if (!formData.workPercentage || formData.workPercentage <= 0.1)
        return false;
    }
    if (
      !isSimflexityEmployee &&
      formData.paymentType === PaymentMethod.SIMFLEXITY_PAY
    ) {
      if (!canSelectSimflexityPay) return false;
    }

    return true;
  };

  useEffect(() => {
    if (isConsultantEmployee || isTraineeEmployee) {
      setFormData((prev) => ({
        ...prev,
        employeeCategory: EmployeeCategory.UNDEFINED,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.employmentType]);

  return (
    <>
      <Modal
        open={props.showUpdateWorkerSalaryModal}
        onClose={() => {
          props.setShowUpdateWorkerSalaryModal(false);
        }}
      >
        <ModalBody style={{ width: 600 }}>
          <TopModal
            setClose={() => props.setShowUpdateWorkerSalaryModal(false)}
            title={`${props.selectedWorkerWithSalary.firstName} ${
              props.selectedWorkerWithSalary.lastName
            }'s ${(() => {
              const s = t("OrganizationalStructure.Employment");
              return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
            })()}`}
          />

          <UpdateSalaryContainer>
            <TextInputWithLabel
              maxLength={24}
              label={t("OrganizationalStructure.Role")}
              required={true}
              value={formData.role}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  role: e.target.value,
                })
              }
              marginBottom={10}
              marginRight={10}
            />

              <DropdownWithLabel
                label={t("OrganizationalStructure.EmploymentType")}
                options={employmentOptions}
                value={formData.employmentType}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    employmentType: isWorkerEmploymentType(value)
                      ? value
                      : undefined,
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseEmploymentType")}
                required
              />
            {!isConsultantEmployee &&
              !isTraineeEmployee &&
              !isSimflexityEmployee && (
                <>
                  <DropdownWithLabel
                    label={t("OrganizationalStructure.EmployeeCategory")}
                    options={employeeCategoryOptions}
                    value={formData.employeeCategory}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        employeeCategory: value,
                      })
                    }
                    required
                  />

                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <TextInputWithLabel
                      maxLength={50}
                      label={t("OrganizationalStructure.EmployeeNumber")}
                      value={formData.employeeNumber?.toString()}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          employeeNumber: Number(e.target.value),
                        })
                      }
                      marginBottom={10}
                      marginRight={10}
                      required
                      width={formData.employeeNumber === 0 ? 180 : 250}
                    />
                    {formData.employeeNumber === 0 && (
                      <ButtonComponent
                        disabled={loading}
                        size={"small"}
                        onClick={() => getEmployeeNumber()}
                        action={true}
                        marginTop={10}
                        marginBottom={20}
                        marginRight={10}
                      >
                        <CustomIcon
                          name="reload"
                          color={Color.White}
                          padding="4px"
                          size="24px"
                        />
                      </ButtonComponent>
                    )}
                  </div>
                </>
              )}
            <DatePickerWithLabel
              label={t("OrganizationalStructure.StartDate")}
              required={true}
              value={formData.startDate ? new Date(formData.startDate) : null}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  startDate: normalizeDateTimeNoFractionNoOffset(
                    formatDate(date),
                  ),
                })
              }
              placeholder={t("OrganizationalStructure.ChooseDate")}
              dateFormat="YYYY-MM-DD"
              monthlySelectableDay={hasEnded ? undefined : 1}
            />
            <DatePickerWithLabel
              label={t("OrganizationalStructure.EndDate")}
              value={formData.endDate ? new Date(formData.endDate) : null}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  endDate: date
                    ? normalizeDateTimeNoFractionNoOffset(formatDate(date))
                    : null,
                })
              }
              placeholder={t("OrganizationalStructure.ChooseDate")}
              dateFormat="YYYY-MM-DD"
              onFocus={() => {
                setFormData((prev) => ({
                  ...prev,
                  endDate: normalizeDateTimeNoFractionNoOffset(
                    formatDate(new Date()),
                  ),
                }));
              }}
              allowClear={!isProjectEmployee && !isTraineeEmployee}
              required={isProjectEmployee || isTraineeEmployee}
            />
            {!isTraineeEmployee && (
              <DropdownWithLabel
                label={t("OrganizationalStructure.SalaryModel")}
                options={salaryOptionsFiltered}
                value={formData.salaryType}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    salaryType: value,
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                required
                disabled={isSimflexityEmployee}
              />
            )}

            {formData.salaryType === WorkerSalaryType.MONTHLY &&
              !isTraineeEmployee &&
              !isSimflexityEmployee && (
                <TextInputWithLabel
                  maxLength={50}
                  label={`${t("OrganizationalStructure.WorkPercentage")} (%)`}
                  required={true}
                  value={
                    formData.workPercentage !== undefined &&
                    formData.workPercentage !== null
                      ? Math.round(formData.workPercentage * 100).toString()
                      : ""
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setFormData({
                        ...formData,
                        workPercentage: 0,
                      });
                      return;
                    }
                    if (!/^\d+$/.test(raw)) return;

                    const clampedPercent = clamp(parseInt(raw, 10), 0, 100);

                    setFormData({
                      ...formData,
                      workPercentage: clampedPercent / 100,
                    });
                  }}
                  type="number"
                  marginRight={10}
                  marginBottom={10}
                />
              )}

            {!isTraineeEmployee && (
              <TextInputWithLabel
                maxLength={7}
                label={
                  formData.salaryType === WorkerSalaryType.MONTHLY
                    ? t("OrganizationalStructure.Month")
                    : t("OrganizationalStructure.Hour")
                }
                value={formData.salary?.toString() || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    salary: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder={t("OrganizationalStructure.AddSalary")}
                type="number"
                required
                marginRight={10}
              />
            )}

            {!isTraineeEmployee && !isSimflexityEmployee && (
              <DropdownWithLabel
                label={t("OrganizationalStructure.TaxTable")}
                options={taxTableOptions}
                value={String(formData.taxTable)}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    taxTable: Number(value),
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                required
              />
            )}

            {!isTraineeEmployee && (
              <DropdownWithLabel
                label={t("ProfileCompany.PaymentSolution")}
                options={paymentTypeOptions}
                value={formData.paymentType}
                disabled={isSimflexityEmployee}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    paymentType: value,
                  })
                }
                required
              />
            )}
          </UpdateSalaryContainer>

          <ButtonComponent
            disabled={!validateFields() || loading}
            size={"small"}
            onClick={handleUpdateSalary}
            action={true}
            marginTop={10}
            marginBottom={20}
            width={200}
            alignRight
          >
            {t("Salary.UpdateSalary")}
          </ButtonComponent>
        </ModalBody>
      </Modal>
    </>
  );
};
